import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../ModalContainer/ModelContainer'
import Loader from '../../Loader/Loader'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

// Created once — loadStripe caches internally, but keep it out of render to avoid re-instantiating.
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY)

// Confirms the SetupIntent (created server-side) against the resident's Stripe customer so the card /
// bank can be charged off-session later (autopay cron, or a future Pay Now with a saved method).
// Mirrors the mobile PaymentSheet capture.
const StripeSetupIntentFields = ({ setupIntentId, preferredType, onSaved, onClose }) => {
  const { t } = useTranslation(['organisation', 'common'])
  const stripe = useStripe()
  const elements = useElements()
  const { displaySnackbar } = useSnackbar()
  const [isSubmitting, setSubmitting] = useState(false)

  const handleSave = async () => {
    if (!stripe || !elements) return
    setSubmitting(true)
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })
      if (error) throw error
      // ponytail: only accept a method that's ready to charge off-session. Manual ACH entry leaves
      // the SetupIntent in requires_action/processing (microdeposit verification) — the backend
      // would reject that id as "not complete". Instant bank verification and cards land 'succeeded'.
      // Upgrade path: support verify-with-microdeposits if manual ACH is ever required.
      if (setupIntent?.status !== 'succeeded') {
        displaySnackbar(t('organisation:payments.methodModal.needsVerification'), true)
        return
      }
      onSaved(setupIntent.id || setupIntentId)
    } catch (error) {
      displaySnackbar(error?.message || t('organisation:payments.methodModal.saveFailed'), true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="autopay-method-form">
      <PaymentElement
        options={{
          layout: 'tabs',
          // only reorders the tabs — doesn't restrict which types are selectable, so the resident
          // can still switch tabs inside the modal if they change their mind.
          ...(preferredType ? { paymentMethodOrder: [preferredType] } : {}),
        }}
      />
      <div className="body-actions">
        <button className="button button-gray" disabled={isSubmitting} onClick={onClose}>
          {t('common:actions.cancel')}
        </button>
        <button
          className="button button-primary"
          disabled={!stripe || isSubmitting}
          onClick={handleSave}
        >
          {isSubmitting
            ? t('organisation:payments.methodModal.saving')
            : t('organisation:payments.methodModal.saveButton')}
        </button>
      </div>
    </div>
  )
}

StripeSetupIntentFields.propTypes = {
  setupIntentId: PropTypes.string,
  preferredType: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

const StripePaymentMethodModal = ({
  isVisible,
  preferredType,
  onClose,
  onSaved,
  createSetupIntent,
}) => {
  const { t } = useTranslation(['organisation'])
  const { displaySnackbar } = useSnackbar()
  const [clientSecret, setClientSecret] = useState(null)
  const [setupIntentId, setSetupIntentId] = useState(null)
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    if (!isVisible) {
      setClientSecret(null)
      setSetupIntentId(null)
      return
    }
    let cancelled = false
    setLoading(true)
    createSetupIntent()
      .then(({ data }) => {
        if (cancelled) return
        setClientSecret(data.clientSecret)
        setSetupIntentId(data.setupIntentId)
      })
      .catch((error) => {
        if (cancelled) return
        displaySnackbar(
          error?.response?.data?.message || t('organisation:payments.methodModal.setupFailed'),
          true,
        )
        onClose()
      })
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [isVisible, createSetupIntent])

  if (!isVisible) return null

  return (
    <ModalContainer
      isVisible={isVisible}
      onClose={onClose}
      headerTitle={t('organisation:payments.addPaymentMethod')}
    >
      <div className="autopay-method-modal">
        {isLoading || !clientSecret ? (
          <Loader style={{ width: 30, height: 30 }} />
        ) : (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <StripeSetupIntentFields
              setupIntentId={setupIntentId}
              preferredType={preferredType}
              onSaved={onSaved}
              onClose={onClose}
            />
          </Elements>
        )}
      </div>
    </ModalContainer>
  )
}

StripePaymentMethodModal.propTypes = {
  createSetupIntent: PropTypes.func.isRequired,
  isVisible: PropTypes.bool.isRequired,
  preferredType: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
}

export default StripePaymentMethodModal
